import {ProjectService} from "./project.service";
import {Component} from "@angular/core";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {Subscription} from "rxjs";
import {masterAgreementStatusValues} from "./master-agreement-status";
import {TARGET_SYSTEM} from "../helpers/app-constants";
import {SmartsheetService} from "../smartsheet/smartsheet.service";
import { DAAS_TARGET_SYSTEMS } from "../helpers/app-constants";
import { PrivilegeViewService } from "../privilege-view/privilege-view.service";
import {DialogService} from "../../src/bootstrap-modal/dialog.service";
import { TextDialogService } from "../text-dialog/text-dialog-service";


@Component({
  selector: "project",
  templateUrl: "./project-show.template.pug",
  providers: [
    ProjectService,
    ConfirmDialogService,
    SmartsheetService,
  ],
})

export class ProjectComponent extends RouteHelper {
  public enableDaas;
  public checkActiveMA;
  public targetSystemList = TARGET_SYSTEM;
  public daasTargetSytemList = DAAS_TARGET_SYSTEMS;
  public  enableProjectDeletionOnRole: boolean = false;
  
  private subscription: Subscription;
  private smartsheetMessage: string;
  error: Error;
  id: string;
  project: any = {};
  projectStatus: string;
  loadingSync: boolean = false;
  synced: boolean = false;
  isFileDownloading: any;
  userRole: any;
  isPurgeMasterRole: boolean = false;
  paramsMap: RouteMapping[] = [
    {fieldName: "id", paramName: "id"} as RouteMapping,
  ];

  userTypes: {key: string, text: string}[] = [
    {key: "transitionLeads", text: "Transition Lead(s): "},
    {key: "accountLeads", text: "Account Lead(s): "},
    {key: "pursuitLead", text: "Pursuit Lead(s): "},
    {key: "discoveryDesignLead", text: "Discovery and Design Lead(s): "},
    {key: "contractAdministrator", text: "Contract Administrator(s): "},
    {key: "productSupportSpecialist", text: "Product Support Specialist(s): "},
  ];

  showRemoveButton: boolean = false;

  getProjectStatus(status, targetSystem) {
    switch (status) {
      case "open":
        return "Transition";
      case "hold":
        return targetSystem !== "daas" && targetSystem !== "midaas" ? "Steady State" : status;
      default:
        return status;
    }
  }

  isEmptyObject(obj) {
    return (obj && Object.keys(obj).length === 0 && obj.constructor === Object);
  }

  setFileDownload(evt) {
    this.isFileDownloading = evt;
  }

  @handleErrors()
  async loadData(): Promise<any> {
    this.enableProjectDeletionOnRole = await this.appSettingsService.getFeaturePermission("enableProjectDeletionOnRole", this.project.id);
    const domain = this.privilegeViewService.getDomainView();
    const {rows} = await this.projectService.getProjects("0", "1", `id:${this.id}`, false, false, false, false, false, "", true, "", domain);
    this.project = rows[0] || {};
    this.projectStatus = this.getProjectStatus(this.project.status, this.project.targetSystem);
    if(this.enableProjectDeletionOnRole){
    this.userRole = this.appSettingsService.user.subscribe((user) => {
      if (user && user.roles && user.roles.length > 0) {
        let showRemoveButton = false;
        user.roles.forEach((role) => {
          if (role.name === "Purge Master" && this.project.currentMaInternalId ) {
            showRemoveButton = true;
          } else if (!this.project.currentMaInternalId && (role.name === "Purge Master" || role.name === "Transition Lead" || role.name === "Super User")) {
            showRemoveButton = true;
          }
          if (role.name == "Purge Master" && this.isPurgeMasterRole == false) {
            this.isPurgeMasterRole = true;
          }
        });
        // Set the flag to show or hide the remove button
        this.showRemoveButton = showRemoveButton;
      }
    });
    }
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["project"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async remove() {
    if (this.checkActiveMA && (this.project.currentMaStatus === masterAgreementStatusValues.itsmOnboarded.nameShort
      || this.project.currentMaStatus === masterAgreementStatusValues.maintenance.nameShort)) {
      throw Error("This project has an active MA and it can't be removed.");
    }

    const warningParts = [];

    if (this.project.siteCount) warningParts.push(`${this.project.siteCount} site(s)`);
    if (this.project.plannedDevices) warningParts.push(`${this.project.plannedDevices} assets(s)`);
    if (this.project.orderCount) warningParts.push(`${this.project.siteCount} orders(s)`);
    if (this.project.deploymentCount) warningParts.push(`${this.project.deploymentCount} deployments(s)`);

    const warning = warningParts.length ? ` The following will be deleted: ${warningParts.join(", ")}.` : "";

    const message = `Are you sure you want to delete this project?${warning}`;
    const result = await this.confirmDialogService.confirm("Confirm", message);

    if (result) {
      await this.projectService.remove(this.project.id).toPromise();
      await this.router.navigate(["/projects"]);
    }
  }

  @handleErrors()
  async purge() {
    let warnings = '';
    let warningMaMsg = '';
    if (this.project.id) {
      warnings += `Project ID: ${this.project.id}\n`;
    }
    if (this.project.opportunityId) {
      warnings += `Opportunity ID: ${this.project.opportunityId}\n`;
    }
    if (this.project.maexternalid) {
      warnings += `Master Agreement External ID: ${this.project.maexternalid}\n`;
      warningMaMsg = `Note: <i>Ensure you have performed necessary deletions/terminations in all downstream assets (ITSM/SAP)</i>`
    }
    if (this.project.currentMaInternalId) {
      warnings += `Master Agreement Internal ID: ${this.project.currentMaInternalId}\n`;
    }
    if(!this.enableProjectDeletionOnRole){
    if (this.checkActiveMA && (this.project.currentMaStatus === masterAgreementStatusValues.itsmOnboarded.nameShort
      || this.project.currentMaStatus === masterAgreementStatusValues.maintenance.nameShort)) {
      throw Error("This project has an active MA and it can't be removed.");
    }}
    const warningParts = [];

    if (this.project.siteCount) warningParts.push(`${this.project.siteCount} site(s)`);
    if (this.project.plannedDevices) warningParts.push(`${this.project.plannedDevices} assets(s)`);
    if (this.project.orderCount) warningParts.push(`${this.project.siteCount} orders(s)`);
    if (this.project.deploymentCount) warningParts.push(`${this.project.deploymentCount} deployments(s)`);

    let warning = warningParts.length ? `The following will be deleted: ${warningParts.join(", ")}.\n` : "";

    let result;
    if(this.enableProjectDeletionOnRole){
    const isMimpOrMimcInEmea = (this.project.targetSystem === "mimp" || this.project.targetSystem === "mimc") && this.project.geoRegionId === "EMEA";
    if (this.project.isMpc || isMimpOrMimcInEmea) {
    //if (((this.project.isMpc || (this.project.targetSystem === "mimc" || this.project.targetSystem === "mimp")) && this.project.geoRegionId == "EMEA") || (this.project.isMpc && this.project.targetSystem === "mpflexr")) {


      warning += `You are deleting a Partner project which has reference in MPC. Please create a MSSI ticket for MPC L3 with below details, so that required action can be taken up in MPC.\n`

      let message = `Are you sure you want to delete this project ?\n${warning}`+`\n${warnings}`+`\n${warningMaMsg}`;
      result = await this.textDialogService.confirm("Confirm", message);
    }
    else{
      let message = `Are you sure you want to delete this project ?\n${warning}`+`\n${warnings}`+`\n${warningMaMsg}`;
      result = await this.confirmDialogService.confirm("Confirm", message);
    }
  }
  else{
    let message = `Are you sure you want to delete this project ?\n${warning}`;
    result = await this.confirmDialogService.confirm("Confirm", message);
  }

    if (result) {
      await this.projectService.purge(this.project.id,result);
      await this.router.navigate(["/projects"]);
    }
  }

  @handleErrors()
  async syncWorkspace(type) {
    this.loadingSync = true;
    this.synced = false;
    let result;
    try {
      result = await this.smartsheetService.syncProject(this.project.id, type);
    } catch (e) {
      this.loadingSync = false;
      this.synced = false;
      throw new Error(e);
    }
    if (!result.success) {
      this.loadingSync = false;
      this.synced = false;
      throw new Error(result.error);
    }
    this.loadingSync = false;
    this.synced = true;
    if (this.synced && type === "push") this.smartsheetMessage = "Updated workspaces!";
    if (this.synced && type === "pull") this.smartsheetMessage = "Project Updated!";
  }

  @handleErrors()
  async unassignMeToProject() {
    const res = await this.projectService.unAssignUserToProject(this.project.id).toPromise();
    if (res && res.success) {
      await this.router.navigate(["/projects"]);
    }
  }

  @handleErrors()
  public async geoRegionChanged(project) {
    await this.projectService.updateGeoRegion(project.id, project.geoRegionId);
    project.geo_region_id = project.geoRegionId; // eslint-disable-line @typescript-eslint/camelcase
  }

  ngOnDestroy() {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    private projectService: ProjectService,
    private confirmDialogService: ConfirmDialogService,
    private textDialogService: TextDialogService,
    private dialogService: DialogService,
    public appSettingsService: AppSettingsService,
    public smartsheetService: SmartsheetService,
    private privilegeViewService: PrivilegeViewService,
  ) {
    super();
    this.subscription = this.appSettingsService.settings.subscribe((settings) => {
      this.enableDaas = settings.enableDaas;
      this.checkActiveMA = settings.checkActiveMA;
    });
  }
}
